import { compact, flatten, isEqual, isNil, mapValues, uniq } from 'lodash';
import type { CallbackInterface } from 'recoil';
import { atom, atomFamily, selector, selectorFamily, waitForAll } from 'recoil';
import type { Fund, Portfolio, PrivatePortfolioNode } from 'venn-api';
import {
  adjustFeesMappingForDescendantNode,
  AnalysisSubject,
  arePortfoliosEqual,
  type BlockSubjectType,
  findPortfolioNodeByFundId,
  getBlockSubjectType,
  getRequestSubjectForSecondaryAnalysisSubject,
  getRequestSubjectFromAnalysisSubject,
  updateFilteredSubjectFields,
} from 'venn-utils';
import { resetOnStudioReset } from '../../effects/signalEffects';
import { isReportState } from './view';
import { allBlockIdsState } from '../grid';
import type {
  BlockId,
  StudioRequestPortfolioCompare,
  StudioRequestSubject,
  Subject,
  SubjectGroupId,
  SubjectWithOptionalFeeAndFilters,
} from '../types';
import { subjectsAreEqual } from '../utils';
import { allocatorAnalysisSubject, openAllocatorSubject, openPrivateAllocatorPortfolio } from './allocator';
import { blockSettings } from './blockSettings';
import { analysisSubjectQuery, analysisSubjects, originalAnalysisSubjectQuery } from './analysisSubjectQuery';
import type { SubjectInputId } from './input-management/subjectInput';
import {
  blockSubjectInputGroups,
  multiSubjectInputGroupSubjects,
  subjectInputGroupName,
  subjectInputGroups,
  subjectInputGroupSubjects,
} from './input-management/subjectInput';

export {
  analysisSubjectQuery,
  analysisSubjects,
  originalAnalysisSubjectQuery,
  getLibrarySearchEntityAnalysisSubjectType,
  rebuildAggregatedPortfolio,
} from './analysisSubjectQuery';

export const blockSubjects = selectorFamily<SubjectWithOptionalFeeAndFilters[], BlockId>({
  key: 'blockSubjects',
  get:
    (id) =>
    ({ get }) => {
      const subjectGroups = get(blockSubjectInputGroups(id));
      const allSubjects = get(waitForAll(subjectGroups.map(subjectInputGroupSubjects)));
      const result = subjectGroups.map((_, subjectGroupIndex) => allSubjects[subjectGroupIndex]!);
      return uniq(result.flat());
    },
});

export const blockAnalysisSubjects = selectorFamily<AnalysisSubject[], SubjectGroupId>({
  key: 'blockAnalysisSubjects',
  get:
    (id) =>
    ({ get }) =>
      get(waitForAll(get(blockSubjects(id)).map((s) => analysisSubjectQuery(s)))),
});

export const modifiedPortfolioForSubject = selectorFamily<Portfolio | undefined, Subject>({
  key: 'modifiedPortfolioForSubject',
  get:
    (subject) =>
    ({ get }) => {
      if (subject.type !== 'portfolio') {
        return undefined;
      }
      const allocatorSubject = get(allocatorAnalysisSubject(subject));
      if (isNil(allocatorSubject) || isNil(allocatorSubject.portfolio)) {
        return undefined;
      }
      // A live aggregated portfolio is read-only in the allocator, so it never has a modified portfolio; and its
      // combined tree is grouping-dependent, so reading the async originalAnalysisSubjectQuery here would suspend
      // every block on a Group by switch. Short-circuit before touching that query.
      if (allocatorSubject.isAggregatedPortfolio) {
        return undefined;
      }
      const originalSubject = get(originalAnalysisSubjectQuery(subject));
      if (isNil(originalSubject) || isNil(originalSubject.portfolio)) {
        return undefined;
      }
      return arePortfoliosEqual(originalSubject.portfolio, allocatorSubject.portfolio)
        ? undefined
        : allocatorSubject.portfolio;
    },
});

/**
 * Stores temporary changes to private portfolio in allocator panel
 * like added/removed investments and changed capital commitment values
 */
export const modifiedPrivatePortfolioForSubject = selectorFamily<PrivatePortfolioNode | undefined, Subject>({
  key: 'modifiedPrivatePortfolioForSubject',
  get:
    (subject) =>
    ({ get }) => {
      if (subject.type !== 'private-portfolio') {
        return undefined;
      }
      const originalPortfolio = get(openPrivateAllocatorPortfolio);
      if (isNil(originalPortfolio) || subject.privatePortfolioId !== originalPortfolio.id) {
        return undefined;
      }
      const allocatorSubject = get(allocatorAnalysisSubject(subject));
      if (isNil(allocatorSubject) || isNil(allocatorSubject.privatePortfolio)) {
        return undefined;
      }

      if (isEqual(originalPortfolio, allocatorSubject.privatePortfolio)) {
        return undefined;
      }

      return allocatorSubject.privatePortfolio;
    },
});

export const portfolioInAllocator = selector<Portfolio | undefined>({
  key: 'portfolioInAllocator',
  get: ({ get }) => {
    const openSubject = get(openAllocatorSubject);
    return openSubject && get(modifiedPortfolioForSubject(openSubject));
  },
});

/**
 * For benchmarks, we don't generate additional `StudioRequestSubject`s for secondary subjects, nor do we include
 * modified portfolios.
 */
export const benchmarkRequestSubjects = selectorFamily<StudioRequestSubject[], Subject[]>({
  key: 'benchmarkRequestSubjects',
  get:
    (benchmarks) =>
    ({ get }) => {
      const benchmarkAnalysisSubjects = get(
        waitForAll(benchmarks.map((benchmark) => originalAnalysisSubjectQuery(benchmark))),
      );
      return compact(benchmarkAnalysisSubjects).map((subject) => getRequestSubjectFromAnalysisSubject(subject));
    },
});

/**
 * Fetch Studio Request subject object without expanding by filtered items.
 */
export const requestSpecificSubjects = selectorFamily<StudioRequestSubject[], SubjectWithOptionalFeeAndFilters[]>({
  key: 'requestSpecificSubjects',
  get:
    (subjects) =>
    ({ get }) => {
      const isReport = get(isReportState);
      return subjects.map((subject) => {
        const modifiedPortfolio = get(modifiedPortfolioForSubject(subject));
        const modifiedPrivatePortfolio = get(modifiedPrivatePortfolioForSubject(subject));
        const analysisSubject = get(analysisSubjectQuery(subject));
        const requestSubject = getRequestSubjectFromAnalysisSubject(analysisSubject);
        return {
          ...requestSubject,
          feesMapping: isReport
            ? adjustFeesMappingForDescendantNode(requestSubject, subject.feesMapping ?? {}, Number(analysisSubject.id))
            : undefined,
          modifiedPortfolio,
          modifiedPrivatePortfolio,
        };
      });
    },
});

const requestSubjectsFromSubject = selectorFamily<StudioRequestSubject[], SubjectWithOptionalFeeAndFilters>({
  key: 'requestSubjectsFromSubject',
  get:
    (subject) =>
    ({ get }) => {
      const isReport = get(isReportState);
      const modifiedPortfolio = get(modifiedPortfolioForSubject(subject));
      const modifiedPrivatePortfolio = get(modifiedPrivatePortfolioForSubject(subject));
      const analysisSubject = get(analysisSubjectQuery(subject));
      const filteredSubjects = expandFilteredSubjects(subject, analysisSubject);

      return compact([
        ...filteredSubjects.map((filteredSubject) => {
          const requestSubject = getRequestSubjectFromAnalysisSubject(filteredSubject);
          return {
            ...updateFilteredSubjectFields(requestSubject),
            feesMapping: isReport
              ? adjustFeesMappingForDescendantNode(
                  requestSubject,
                  subject.feesMapping ?? {},
                  Number(analysisSubject.id),
                )
              : undefined,
            modifiedPortfolio,
            modifiedPrivatePortfolio,
            isFiltered: !!subject.filteredSubjectIds?.length,
          };
        }),
        getRequestSubjectForSecondaryAnalysisSubject(analysisSubject),
      ]);
    },
});

const expandFilteredSubjects = (subject: SubjectWithOptionalFeeAndFilters, analysisSubject: AnalysisSubject) => {
  if (!subject.filteredSubjectIds || subject.filteredSubjectIds.length === 0 || !analysisSubject.portfolio) {
    return [analysisSubject];
  }
  return subject.filteredSubjectIds.map((filteredSubjectId) => {
    const numericId = Number(filteredSubjectId);
    // If numericId is NaN, it's a fund ID (UUID string) - need to find the portfolio node containing this fund.
    // This happens when portfolio filtering selects an investment node directly.
    const strategyId = Number.isNaN(numericId)
      ? findPortfolioNodeByFundId(analysisSubject.portfolio!, filteredSubjectId)
      : numericId;

    return new AnalysisSubject(analysisSubject.portfolio!, 'portfolio', analysisSubject.proxySetId, {
      ...analysisSubject.getOptionsCopy(),
      strategyId,
    });
  });
};

export const multiSubjectInputGroupAnalysisSubjects = selectorFamily<
  Record<SubjectInputId, AnalysisSubject[]>,
  SubjectInputId[]
>({
  key: 'multiSubjectInputGroupRequestSubjects',
  get:
    (groupIds) =>
    ({ get }) =>
      mapValues(get(multiSubjectInputGroupSubjects(groupIds)), (subjects) => get(analysisSubjects(subjects))),
});

export const excludedFees = selectorFamily<Record<SubjectInputId, string[][]>, SubjectInputId[]>({
  key: 'excludedFees',
  get:
    (groupIds) =>
    ({ get }) =>
      mapValues(get(multiSubjectInputGroupSubjects(groupIds)), (subjects) =>
        subjects.map((subject) => {
          const ownId =
            subject.type === 'portfolio'
              ? subject.portfolioId.toString()
              : subject.type === 'fund'
                ? subject.fundId
                : undefined;
          return Object.keys(subject.feesMapping ?? []).filter(
            (key) => key !== ownId && subject.feesMapping?.[key] === 0,
          );
        }),
      ),
});

export const requestSubjects = selectorFamily<StudioRequestSubject[], SubjectWithOptionalFeeAndFilters[]>({
  key: 'requestSubjects',
  get:
    (subjects) =>
    ({ get }) => {
      return flatten(get(waitForAll(subjects.map((subject) => requestSubjectsFromSubject(subject)))));
    },
});

const usedSubjects = selectorFamily<StudioRequestSubject[], [BlockId, SubjectWithOptionalFeeAndFilters[]]>({
  key: 'usedSubjects',
  get:
    ([id, subjects]) =>
    ({ get }) => {
      const supportedSubjectTypes = get(blockSupportedSubjectTypes(id));
      return get(requestSubjects(subjects)).filter((subject) => {
        const subjectType = getBlockSubjectType(subject);
        return supportedSubjectTypes.includes(subjectType);
      });
    },
});

const blockSupportedSubjectTypes = selectorFamily<BlockSubjectType[], BlockId>({
  key: 'blockSupportedSubjectTypes',
  get:
    (id) =>
    ({ get }) => {
      return get(blockSettings(id)).supportedSubjectTypes;
    },
});

export const subjectGroupRequestSubjects = selectorFamily<StudioRequestSubject[], [BlockId, SubjectInputId]>({
  key: 'subjectGroupRequestSubjects',
  get:
    ([id, groupId]) =>
    ({ get }) => {
      const subjects = get(subjectInputGroupSubjects(groupId));
      return get(usedSubjects([id, subjects]));
    },
});

export const blockRequestSubjects = selectorFamily<StudioRequestSubject[], BlockId>({
  key: 'blockRequestSubjects',
  get:
    (id) =>
    ({ get }) => {
      const subjects = get(blockSubjects(id));
      return get(usedSubjects([id, subjects]));
    },
});

/** use this in a useRecoilCallback hook to delete a subject group for a given subject group id */
export const onSubjectInputDelete =
  ({ snapshot, set, reset }: CallbackInterface) =>
  async (inputId: SubjectInputId) => {
    set(subjectInputGroups, (current) => current.filter((id) => id !== inputId));

    reset(subjectInputGroupName(inputId));
    reset(subjectInputGroupSubjects(inputId));

    const blockIds = await snapshot.getPromise(allBlockIdsState);
    blockIds.forEach((blockId) =>
      set(blockSubjectInputGroups(blockId), (current) => current.filter((id) => id !== inputId)),
    );
  };

/**
 * It is possible for subjects to be deleted from Venn that are contained within a saved view
 * inaccessibleSubjectsState stores any subjects that are no longer accessible in the view
 */
export const inaccessibleSubjectsState = atom<Subject[]>({
  key: 'inaccessibleSubjectsState',
  default: [],
  effects: [resetOnStudioReset],
});

export const inaccessibleSubjectsDismissed = atomFamily<Subject[] | undefined, string>({
  key: 'inaccessibleSubjectsDismissed',
  default: undefined,
});

export const showInaccessibleSubjectsModal = selectorFamily<boolean, string>({
  key: 'showInaccessibleSubjectsModal',
  get:
    (id) =>
    ({ get }) => {
      const inaccessibleSubjects = get(inaccessibleSubjectsState);
      if (inaccessibleSubjects.length === 0) {
        return false;
      }
      const lastDismissedSubjects = get(inaccessibleSubjectsDismissed(id));
      return (
        inaccessibleSubjects.length !== lastDismissedSubjects?.length ||
        inaccessibleSubjects.some((subject, idx) => !subjectsAreEqual(subject, lastDismissedSubjects[idx]))
      );
    },
  set:
    (id) =>
    ({ get, set }, value) => {
      if (value) {
        set(inaccessibleSubjectsDismissed(id), undefined);
      } else {
        set(inaccessibleSubjectsDismissed(id), get(inaccessibleSubjectsState));
      }
    },
});

export const getBenchmarkSubject = (
  compare: StudioRequestPortfolioCompare,
  proxySetId: string | undefined,
): AnalysisSubject => {
  if (compare.fundId) {
    const fund = { id: compare.fundId, name: compare.name } as Fund;
    return new AnalysisSubject(fund, 'investment', proxySetId);
  }
  const portfolio = {
    id: compare.portfolioId,
    name: compare.name,
    children: [],
    demo: false,
    draft: true,
    master: false,
    closingAllocationsTs: [],
    historical: compare.historical,
    absNetAssets: 0,
    designation: 'DEFAULT',
    lots: [],
    taxStatuses: [],
  } as Portfolio;
  return new AnalysisSubject(portfolio, 'portfolio', proxySetId, { instrumentIdOnly: true });
};
